import type { FeatureLimits } from '@/types/api';

/**
 * Where a limited allowance stands: fine, close to used up, or used up.
 *
 * Upgrade prompts are shown for `near` and `reached` and nowhere else, so this is the one place
 * that decides what "nearly" means. A fifth of the allowance, or the last one — which makes it the
 * third monitor of three and the twentieth run of twenty-five. An allowance of one is never near:
 * it is either free or used.
 */
export type UsageLevel = 'ok' | 'near' | 'reached';

export function usageLevel(used: number, limit: number | null): UsageLevel {
  // A negative limit is the platform's sentinel for no ceiling; null is the same thing, said by an
  // endpoint that has no ceiling to report.
  if (limit === null || limit < 0) return 'ok';
  if (used >= limit) return 'reached';

  const remaining = limit - used;

  return used > 0 && remaining <= Math.max(1, Math.floor(limit / 5)) ? 'near' : 'ok';
}

/** "1 server", "3 monitors": a count and its noun, the way the limit reads in a sentence. */
export function countOf(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The trial's allowances in words, in the order somebody would say them.
 *
 * Read from the trial plan's own features — the ones the API enforces — so an operator who changes
 * the trial in the catalogue changes this sentence as well. The same wording as the trial emails.
 */
export function trialLimitsInWords(limits: FeatureLimits): string[] {
  const words: string[] = [];
  const nouns: [string, string, string][] = [
    ['servers', 'server', 'servers'],
    ['monitors', 'monitor', 'monitors'],
    ['backup_sources', 'backup source', 'backup sources'],
    ['command_runs', 'Command Runner run', 'Command Runner runs'],
  ];

  for (const [key, one, many] of nouns) {
    const limit = limits[key];

    if (typeof limit === 'number' && limit >= 1) {
      words.push(countOf(limit, one, many));
    }
  }

  const team = limits.team_members;

  if (team === 1) words.push('one person');
  else if (typeof team === 'number' && team > 1) words.push(`${team} people`);

  return words;
}

/** "a, b and c". */
export function listInWords(items: string[]): string {
  if (items.length <= 1) return items.join('');

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** "24 September 2026", in the reader's own locale. */
export function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * When the trial ends, as precisely as is useful.
 *
 * A date is enough while there are days to spare. On the last two days the time matters — "ends
 * tomorrow" at 23:00 and at 09:00 are different amounts of time to decide — so it is added.
 */
export function trialEndsPhrase(endsAt: string, now: Date = new Date()): string {
  const end = new Date(endsAt);
  const time = end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(end) - startOfDay(now)) / 86_400_000);

  if (days <= 0) return `Ends today at ${time}`;
  if (days === 1) return `Ends tomorrow at ${time}`;

  return `Ends ${longDate(endsAt)}`;
}

/** "5 days left", "1 day left". */
export function daysLeftPhrase(days: number): string {
  return `${countOf(days, 'day', 'days')} left`;
}
